import React, { useState, useRef, useEffect, useCallback, useId } from "react";
import { useData } from "../../providers/DataProvider";
import { RegionProps } from "../../providers/types";

const RegionSwitcher: React.FC = () => {
  const { regions, selectedRegion, updateSelectedRegion, isRegionLoading } = useData();

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const dropdownId = useId();

  // Auto-select the first available region on initial load.
  // Skipped if a region is already set (e.g. returning from navigation)
  // or if the regions list hasn't loaded yet.
  // Intentionally excludes `selectedRegion` from deps — we only want this
  // to fire when regions first becomes available, not on every region change.
  useEffect(() => {
    if (selectedRegion || regions.length === 0) return;
    updateSelectedRegion(regions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  // Close on outside click — pointerdown fires before click, prevents flicker
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  // Focus first menu item when menu opens
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const firstItem = dropdownRef.current.querySelector<HTMLElement>(
        '[role="menuitem"]:not([disabled])'
      );
      firstItem?.focus();
    }
  }, [isOpen]);

  // Return focus to trigger on close
  const closeMenu = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const handleRegionSelect = useCallback(
    (region: RegionProps) => {
      updateSelectedRegion(region);
      closeMenu();
    },
    [updateSelectedRegion, closeMenu]
  );

  const getMenuItems = useCallback(() => {
    if (!dropdownRef.current) return [];
    return Array.from(
      dropdownRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]')
    );
  }, []);

  const handleMenuKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const items = getMenuItems();
      if (items.length === 0) return;

      const currentIndex = items.findIndex((item) =>
        item.contains(document.activeElement)
      );

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          closeMenu();
          break;

        case "ArrowDown":
          e.preventDefault();
          items[(currentIndex + 1 + items.length) % items.length]?.focus();
          break;

        case "ArrowUp":
          e.preventDefault();
          items[(currentIndex - 1 + items.length) % items.length]?.focus();
          break;

        case "Home":
          e.preventDefault();
          items[0]?.focus();
          break;

        case "End":
          e.preventDefault();
          items[items.length - 1]?.focus();
          break;

        case "Tab":
          closeMenu();
          break;
      }
    },
    [closeMenu, getMenuItems]
  );

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        closeMenu();
      }
      if ((e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") && !isOpen) {
        e.preventDefault();
        setIsOpen(true);
      }
    },
    [isOpen, closeMenu]
  );

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !isRegionLoading && setIsOpen((v) => !v)}
        onKeyDown={handleTriggerKeyDown}
        disabled={isRegionLoading}
        className="flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out disabled:opacity-75 disabled:cursor-not-allowed"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={dropdownId}
        aria-busy={isRegionLoading}
      >
        {isRegionLoading ? (
          <>
            <svg
              className="animate-spin w-4 h-4 mr-2 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
              />
            </svg>
            <span>Loading…</span>
          </>
        ) : (
          <>
            <span className="mr-2">
              {selectedRegion ? selectedRegion.regionName : "Select Region"}
            </span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {isOpen && (
        <div
          id={dropdownId}
          ref={dropdownRef}
          role="menu"
          tabIndex={-1}
          onKeyDown={handleMenuKeyDown}
          className="absolute left-0 mt-2 py-2 w-48 bg-white rounded-md shadow-xl z-20"
        >
          {regions.length === 0 ? (
            <button
              type="button"
              disabled
              className="block px-4 py-2 text-sm text-gray-400 w-full text-left cursor-not-allowed"
              role="menuitem"
              tabIndex={-1}
            >
              No regions
            </button>
          ) : (
            regions.map((region) => (
              <button
                key={region.regionId}
                type="button"
                onClick={() => handleRegionSelect(region)}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition duration-150 ease-in-out"
                role="menuitem"
                tabIndex={-1}
              >
                {region.regionName}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default RegionSwitcher;